import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import amsProjectService from '../../../../services/amsProjectService';
import RightDrawer from '../../../../shared/components/form/RightDrawer';
import PageBreadcrumb from '../../../../shared/PageBreadCrumb';
import AmsProjectTable from '../../components/AmsProjectTable';
import { isProjectInfoComplete } from '../../../project/utils/projectStatusUtils';
import AmsProjectAssetManagement from '../../components/AmsProjectAssetManagement';
import ProjectProgressHeader from '../../../project/components/ProjectProgressHeader';
import useDebounce from '../../../../hooks/useDebounce';

export default function AmsProjects() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [projects, setProjects] = useState<any[]>([]);
    const [totalProjects, setTotalProjects] = useState<number>(0);
    const [selectedProject, setSelectedProject] = useState<any>(null);
    const [search, setSearch] = useState<string>("");
    // Debounce the search so fetches fire after typing stops — avoids per-keystroke
    // requests racing (an earlier broad response landing last and clobbering results).
    const debouncedSearch = useDebounce(search, 400);
    const [projectTypeId, setProjectTypeId] = useState<string>("");
    const [location, setLocation] = useState<string>("");
    const [approvalStatus, setApprovalStatus] = useState<string>("");
    const [page, setPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [sortField, setSortField] = useState<string>("createdAt");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Asset drawer state
    const [openProjectAssetsDrawer, setOpenProjectAssetsDrawer] = useState(false);
    const [progressRefreshKey, setProgressRefreshKey] = useState(0);

    const fetchProjects = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params: any = {
                page,
                limit: itemsPerPage,
                sortBy: sortField,
                sortOrder: sortOrder,
            };

            if (debouncedSearch) params.search = debouncedSearch; // partial (regex) match on name/id/remarks
            if (projectTypeId) params.projectTypeId = projectTypeId;
            if (location) params.locationId = location;

            if (approvalStatus === 'started') {
                params.projectStatus = 'started';
            } else if (approvalStatus === 'completed') {
                params.approvalStatus = 'approved';
            } else if (approvalStatus) {
                params.approvalStatus = approvalStatus;
            }

            const result = await amsProjectService.getProjects(params);
            // Backend returns paginated result: { success, data: { docs: [], totalDocs, ... } }
            const resultData = result.data as any;
            const list = Array.isArray(resultData) ? resultData : (resultData?.docs || []);
            setProjects(list);
            setTotalProjects(resultData?.totalDocs || result.meta?.totalDocs || 0);

            setSelectedProject((prev: any) => {
                if (!prev?._id && !prev?.id) return prev;
                const id = prev._id || prev.id;
                const updated = list.find((p: any) => (p._id || p.id) === id);
                return updated ?? prev;
            });
        } catch (err: any) {
            console.error("Failed to fetch projects", err);
            setError(err.message || "Failed to fetch projects");
            setProjects([]);
            setTotalProjects(0);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, projectTypeId, location, approvalStatus, page, itemsPerPage, sortField, sortOrder]);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, projectTypeId, location, approvalStatus, itemsPerPage]);

    useEffect(() => {
        const selectedProjectId = searchParams.get('selectedProject');

        if (selectedProjectId && projects.length > 0) {
            const project = projects.find(p => p._id === selectedProjectId || p.id === selectedProjectId);
            if (project && (!selectedProject || selectedProject._id !== selectedProjectId)) {
                setSelectedProject(project);
                setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.delete('selectedProject');
                    return newParams;
                });
            }
        }
    }, [projects, searchParams, setSearchParams, selectedProject]);

    const handleAdd = () => {
        navigate('/projects/create');
    };

    const handleEdit = (project: any) => {
        navigate(`/projects/edit/${project._id || project.id}`);
    };

    const handleSelect = async (project: any) => {
        setSelectedProject(project);
    };

    const handleOpenAssetDrawer = (project: any) => {
        if (!isProjectInfoComplete(project)) {
            const id = project._id || project.id;
            if (id) navigate(`/projects/edit/${id}?completeSetup=1`);
            return;
        }
        setSelectedProject(project);
        setOpenProjectAssetsDrawer(true);
    };

    const triggerProgressRefresh = useCallback(() => {
        setProgressRefreshKey((prev) => prev + 1);
    }, []);

    return (
        <div className="max-w-9xl mx-auto space-y-6 relative">
            <PageBreadcrumb pageTitle="AMS Project Management" />

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    {error}
                </div>
            )}

            <AmsProjectTable
                projects={projects}
                search={search}
                setSearch={setSearch}
                projectTypeId={projectTypeId}
                setProjectTypeId={setProjectTypeId}
                location={location}
                setLocation={setLocation}
                approvalStatus={approvalStatus}
                setApprovalStatus={setApprovalStatus}
                page={page}
                setPage={setPage}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                totalProjects={totalProjects}
                onEdit={handleEdit}
                onAdd={handleAdd}
                onSelect={handleSelect}
                onOpenAssetDrawer={handleOpenAssetDrawer}
                selectedProject={selectedProject}
                onRefresh={fetchProjects}
                sortField={sortField}
                sortOrder={sortOrder}
                setSortField={setSortField}
                setSortOrder={setSortOrder}
                loading={loading}
            />

            <RightDrawer
                open={openProjectAssetsDrawer}
                title={selectedProject ? `${selectedProject.projectName || selectedProject.name} - Project Assets` : 'Project Assets'}
                onClose={() => {
                    setOpenProjectAssetsDrawer(false);
                    setSelectedProject(null);
                }}
                size="full"
            >
                <div className="p-0">
                    {selectedProject && (
                        <>
                            {/* Project-level progress + "Submit to Manager" gate (same as the
                                /projects/view Equipment & Assets modal — kept consistent). */}
                            <div className="border-b border-slate-200">
                                <ProjectProgressHeader
                                    project={selectedProject}
                                    refreshSignal={progressRefreshKey}
                                    isAms={true}
                                    onRefresh={() => {
                                        fetchProjects();
                                        triggerProgressRefresh();
                                    }}
                                />
                            </div>
                            <div className="p-6">
                                <AmsProjectAssetManagement
                                    project={selectedProject}
                                    onEquipmentUpdate={() => {
                                        fetchProjects();
                                        triggerProgressRefresh();
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>
            </RightDrawer>

        </div>
    );
}
